import React from 'react';
import { useWaveSurfer } from './useWaveSurfer';
import type { LibraryFile } from './types';
import { getApiUrl } from '../api';

type Band = {
  id: 'low' | 'mid' | 'high';
  name: string;
  muted: boolean;
  solo: boolean;
};

type Slice = {
  id: string;
  start: number;
  end: number;
  label: string;
};

const IconSetStart = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6 4h2v16H6z" />
    <path d="M18 5v14l-8-7z" />
  </svg>
);

const IconSetEnd = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M16 4h2v16h-2z" />
    <path d="M6 5v14l8-7z" />
  </svg>
);

const IconPlay = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <polygon points="7,5 19,12 7,19" />
  </svg>
);

const IconAdd = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M11 5h2v14h-2z" />
    <path d="M5 11h14v2H5z" />
  </svg>
);

const IconRemove = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M6 6l12 12M18 6l-12 12"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2"
    />
  </svg>
);

const IconDownload = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 4v10"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M8 10l4 4 4-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path d="M5 18h14v2H5z" />
  </svg>
);

type AdvancedAudioEditorProps = {
  file: LibraryFile;
  isOpen: boolean;
  onClose: () => void;
  themeKey: string;
};

const AdvancedAudioEditor = ({
  file,
  isOpen,
  onClose,
  themeKey,
}: AdvancedAudioEditorProps) => {
  const audioUrl = getApiUrl(
    `/api/stream?path=${encodeURIComponent(file.path)}`
  );
  const {
    waveformRef,
    isReady,
    isPlaying,
    error,
    playPause,
    setPlaybackRate,
    stop,
    getCurrentTime,
    setVolume,
    getDecodedData,
    getDuration,
    playSegment,
    on,
    off,
  } = useWaveSurfer(isOpen ? audioUrl : null, { height: 200 }, themeKey);
  const [pitchSemitones, setPitchSemitones] = React.useState(0);
  const [speedPercent, setSpeedPercent] = React.useState(100);
  const [isEqReady, setIsEqReady] = React.useState(false);
  const [eqEnabled, setEqEnabled] = React.useState(false);
  const [duration, setDuration] = React.useState(0);
  const [cropStart, setCropStart] = React.useState(0);
  const [cropEnd, setCropEnd] = React.useState(0);
  const [slices, setSlices] = React.useState<Slice[]>([]);
  const [bands, setBands] = React.useState<Band[]>([
    { id: 'low', name: 'Low', muted: false, solo: false },
    { id: 'mid', name: 'Mid', muted: false, solo: false },
    { id: 'high', name: 'High', muted: false, solo: false },
  ]);
  const eqActive = React.useMemo(() => eqEnabled && isEqReady, [eqEnabled, isEqReady]);
  const eqHasOutput = React.useMemo(() => {
    if (!eqActive) {
      return false;
    }
    const anySolo = bands.some((band) => band.solo);
    if (anySolo) {
      return bands.some((band) => band.solo);
    }
    return bands.some((band) => !band.muted);
  }, [bands, eqActive]);

  const audioContextRef = React.useRef<AudioContext | null>(null);
  const decodeContextRef = React.useRef<AudioContext | null>(null);
  const bufferRef = React.useRef<AudioBuffer | null>(null);
  const originalBufferRef = React.useRef<AudioBuffer | null>(null);
  const originalBufferPromiseRef = React.useRef<Promise<AudioBuffer | null> | null>(null);
  const sourceRef = React.useRef<AudioBufferSourceNode | null>(null);
  const gainNodesRef = React.useRef<Record<string, GainNode>>({});
  const playbackRateRef = React.useRef(1);

  const ensureAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
    }
    return audioContextRef.current;
  };

  const ensureDecodeContext = () => {
    if (!decodeContextRef.current) {
      decodeContextRef.current = new AudioContext();
    }
    return decodeContextRef.current;
  };

  const loadOriginalBuffer = React.useCallback(async () => {
    if (originalBufferRef.current) {
      return originalBufferRef.current;
    }
    if (!audioUrl) {
      return null;
    }
    if (originalBufferPromiseRef.current) {
      return originalBufferPromiseRef.current;
    }
    const promise = (async () => {
      const response = await fetch(audioUrl);
      const arrayBuffer = await response.arrayBuffer();
      const context = ensureDecodeContext();
      if (context.state === 'suspended') {
        await context.resume();
      }
      const decoded = await context.decodeAudioData(arrayBuffer.slice(0));
      originalBufferRef.current = decoded;
      return decoded;
    })()
      .catch(() => null)
      .finally(() => {
        originalBufferPromiseRef.current = null;
      });
    originalBufferPromiseRef.current = promise;
    return promise;
  }, [audioUrl]);

  const updateBandGains = React.useCallback(() => {
    const anySolo = bands.some((band) => band.solo);
    bands.forEach((band) => {
      const node = gainNodesRef.current[band.id];
      if (!node) {
        return;
      }
      const shouldPlay = anySolo ? band.solo : !band.muted;
      node.gain.value = shouldPlay ? 1 : 0;
    });
  }, [bands]);

  const stopEqPlayback = React.useCallback(() => {
    if (sourceRef.current) {
      sourceRef.current.stop();
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
  }, []);

  const startEqPlayback = React.useCallback(
    (startTime: number, endTime?: number) => {
      const buffer = bufferRef.current;
      if (!buffer) {
        return;
      }
      const context = ensureAudioContext();
      if (context.state === 'suspended') {
        context.resume();
      }
      stopEqPlayback();

      const source = context.createBufferSource();
      source.buffer = buffer;
      source.playbackRate.value = playbackRateRef.current;

      const lowpass = context.createBiquadFilter();
      lowpass.type = 'lowpass';
      lowpass.frequency.value = 200;

      const bandpass = context.createBiquadFilter();
      bandpass.type = 'bandpass';
      bandpass.frequency.value = 800;
      bandpass.Q.value = 0.7;

      const highpass = context.createBiquadFilter();
      highpass.type = 'highpass';
      highpass.frequency.value = 2000;

      const lowGain = context.createGain();
      const midGain = context.createGain();
      const highGain = context.createGain();

      source.connect(lowpass);
      source.connect(bandpass);
      source.connect(highpass);

      lowpass.connect(lowGain).connect(context.destination);
      bandpass.connect(midGain).connect(context.destination);
      highpass.connect(highGain).connect(context.destination);

      gainNodesRef.current = {
        low: lowGain,
        mid: midGain,
        high: highGain,
      };

      updateBandGains();

      source.start(0, startTime);
      if (typeof endTime === 'number') {
        const length = Math.max(0, endTime - startTime);
        if (length > 0) {
          source.stop(context.currentTime + length / playbackRateRef.current);
        }
      }
      sourceRef.current = source;
    },
    [stopEqPlayback, updateBandGains]
  );

  const toggleBandMute = React.useCallback((id: Band['id']) => {
    setBands((current) =>
      current.map((band) =>
        band.id === id ? { ...band, muted: !band.muted } : band
      )
    );
  }, []);

  const toggleBandSolo = React.useCallback((id: Band['id']) => {
    setBands((current) =>
      current.map((band) => {
        if (band.id === id) {
          return { ...band, solo: !band.solo };
        }
        return { ...band, solo: false };
      })
    );
  }, []);

  React.useEffect(() => {
    const pitchFactor = Math.pow(2, pitchSemitones / 12);
    const speedFactor = speedPercent / 100;
    const rate = pitchFactor * speedFactor;
    playbackRateRef.current = rate;
    setPlaybackRate(rate);
    if (sourceRef.current) {
      sourceRef.current.playbackRate.value = rate;
    }
  }, [pitchSemitones, speedPercent, setPlaybackRate]);

  React.useEffect(() => {
    setPitchSemitones(0);
    setSpeedPercent(100);
    setIsEqReady(false);
    setEqEnabled(false);
    bufferRef.current = null;
    originalBufferRef.current = null;
    originalBufferPromiseRef.current = null;
    stopEqPlayback();
    setDuration(0);
    setCropStart(0);
    setCropEnd(0);
    setSlices([]);
    setBands([
      { id: 'low', name: 'Low', muted: false, solo: false },
      { id: 'mid', name: 'Mid', muted: false, solo: false },
      { id: 'high', name: 'High', muted: false, solo: false },
    ]);
  }, [file.id, stopEqPlayback]);

  React.useEffect(() => {
    if (!isReady) {
      setIsEqReady(false);
      setDuration(0);
      setCropStart(0);
      setCropEnd(0);
      return;
    }
    const buffer = getDecodedData();
    if (buffer) {
      bufferRef.current = buffer;
      setIsEqReady(true);
    }
    const fileDuration = getDuration();
    if (fileDuration > 0) {
      setDuration(fileDuration);
      setCropStart((current) => Math.min(current, fileDuration));
      setCropEnd((current) =>
        current > 0 ? Math.min(current, fileDuration) : fileDuration
      );
    }
  }, [isReady, getDecodedData, getDuration]);

  React.useEffect(() => {
    updateBandGains();
  }, [updateBandGains]);

  React.useEffect(() => {
    setVolume(eqActive ? 0 : 1);
  }, [eqActive, setVolume]);

  React.useEffect(() => {
    if (!eqActive) {
      return;
    }
    const handleSeek = () => {
      if (!isPlaying) {
        return;
      }
      stopEqPlayback();
      if (eqHasOutput) {
        startEqPlayback(getCurrentTime());
      }
    };
    on('seek', handleSeek);
    on('interaction', handleSeek);
    return () => {
      off('seek', handleSeek);
      off('interaction', handleSeek);
    };
  }, [
    eqActive,
    eqHasOutput,
    isPlaying,
    getCurrentTime,
    on,
    off,
    startEqPlayback,
    stopEqPlayback,
  ]);

  React.useEffect(() => {
    if (!eqActive || !isPlaying || !eqHasOutput) {
      stopEqPlayback();
      return;
    }
    startEqPlayback(getCurrentTime());
  }, [
    eqActive,
    eqHasOutput,
    isPlaying,
    getCurrentTime,
    startEqPlayback,
    stopEqPlayback,
  ]);

  React.useEffect(() => {
    if (!isOpen) {
      stopEqPlayback();
      stop();
    }
  }, [isOpen, stopEqPlayback, stop]);

  const formatTime = (value: number) => {
    if (!Number.isFinite(value)) {
      return '0:00';
    }
    const minutes = Math.floor(value / 60);
    const seconds = Math.floor(value % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const playSegmentWithEq = React.useCallback(
    (start: number, end?: number) => {
      if (!isReady) {
        return;
      }
      if (eqActive && eqHasOutput) {
        startEqPlayback(start, end);
      } else {
        stopEqPlayback();
      }
      playSegment(start, end);
    },
    [eqActive, eqHasOutput, isReady, playSegment, startEqPlayback, stopEqPlayback]
  );

  const handleCropStart = (value: number) => {
    const clamped = Math.max(0, Math.min(value, duration));
    setCropStart(clamped);
    setCropEnd((current) => Math.max(clamped, current));
  };

  const handleCropEnd = (value: number) => {
    const clamped = Math.max(0, Math.min(value, duration));
    setCropEnd(clamped);
    setCropStart((current) => Math.min(current, clamped));
  };

  const setStartToPlayhead = () => {
    handleCropStart(getCurrentTime());
  };

  const setEndToPlayhead = () => {
    handleCropEnd(getCurrentTime());
  };

  const addSlice = () => {
    if (!duration) {
      return;
    }
    const start = Math.min(cropStart, cropEnd);
    const end = Math.max(cropStart, cropEnd);
    if (end - start <= 0.02) {
      return;
    }
    setSlices((current) => [
      ...current,
      {
        id: `slice-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        start,
        end,
        label: `Slice ${current.length + 1}`,
      },
    ]);
  };

  const removeSlice = (id: string) => {
    setSlices((current) => current.filter((slice) => slice.id !== id));
  };

  const ensureMp3Encoder = async () => {
    const module = await import('lamejs');
    const Mp3Encoder =
      module.Mp3Encoder ?? module.default?.Mp3Encoder ?? module.default;
    if (!(globalThis as { MPEGMode?: unknown }).MPEGMode) {
      const mpegModule = await import('lamejs/src/js/MPEGMode');
      (globalThis as { MPEGMode?: unknown }).MPEGMode =
        mpegModule.default ?? mpegModule;
    }
    if (!(globalThis as { Lame?: unknown }).Lame) {
      const lameModule = await import('lamejs/src/js/Lame');
      (globalThis as { Lame?: unknown }).Lame = lameModule.default ?? lameModule;
    }
    if (!(globalThis as { BitStream?: unknown }).BitStream) {
      const bitstreamModule = await import('lamejs/src/js/BitStream');
      (globalThis as { BitStream?: unknown }).BitStream =
        bitstreamModule.default ?? bitstreamModule;
    }
    return Mp3Encoder;
  };

  const downloadSliceMp3 = React.useCallback(
    async (slice: Slice) => {
      const buffer = await loadOriginalBuffer();
      if (!buffer) {
        return;
      }
      const Mp3Encoder = await ensureMp3Encoder();
      if (!Mp3Encoder) {
        return;
      }
      const sampleRate = buffer.sampleRate;
      const startSample = Math.floor(slice.start * sampleRate);
      const endSample = Math.floor(slice.end * sampleRate);
      const safeStart = Math.max(0, Math.min(startSample, buffer.length));
      const safeEnd = Math.max(0, Math.min(endSample, buffer.length));
      if (safeEnd <= safeStart) {
        return;
      }
      const channels = buffer.numberOfChannels;
      const leftChannel = buffer.getChannelData(0).subarray(safeStart, safeEnd);
      const rightChannel =
        channels > 1
          ? buffer.getChannelData(1).subarray(safeStart, safeEnd)
          : null;
      const encoder = new Mp3Encoder(channels, sampleRate, 128);
      const blockSize = 1152;
      const mp3Data: Uint8Array[] = [];
      for (let i = 0; i < leftChannel.length; i += blockSize) {
        const left = leftChannel.subarray(i, i + blockSize);
        const leftBuffer = new Int16Array(left.length);
        for (let j = 0; j < left.length; j += 1) {
          const sample = Math.max(-1, Math.min(1, left[j]));
          leftBuffer[j] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
        }
        if (channels === 1) {
          const chunk = encoder.encodeBuffer(leftBuffer);
          if (chunk.length) {
            mp3Data.push(chunk);
          }
        } else if (rightChannel) {
          const right = rightChannel.subarray(i, i + blockSize);
          const rightBuffer = new Int16Array(right.length);
          for (let j = 0; j < right.length; j += 1) {
            const sample = Math.max(-1, Math.min(1, right[j]));
            rightBuffer[j] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
          }
          const chunk = encoder.encodeBuffer(leftBuffer, rightBuffer);
          if (chunk.length) {
            mp3Data.push(chunk);
          }
        }
      }
      const endChunk = encoder.flush();
      if (endChunk.length) {
        mp3Data.push(endChunk);
      }
      const blob = new Blob(mp3Data as BlobPart[], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      const baseName = file.name.replace(/\.[^/.]+$/, '');
      const safeLabel = slice.label.replace(/\s+/g, '-').toLowerCase();
      const link = document.createElement('a');
      link.href = url;
      link.download = `${baseName}-${safeLabel}.mp3`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    },
    [file.name, loadOriginalBuffer]
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div className="advanced-editor" role="dialog" aria-modal="true">
      <div className="advanced-editor__backdrop" onClick={onClose} />
      <div className="advanced-editor__panel">
        <header className="advanced-editor__header">
          <div>
            <p className="eyebrow">Advanced editor</p>
            <h2>{file.name}</h2>
          </div>
          <button className="ghost" type="button" onClick={onClose}>
            Close
          </button>
        </header>

        <section className="advanced-editor__transport">
          <button
            className="transport"
            type="button"
            onClick={() => {
              if (eqActive) {
                if (isPlaying) {
                  stopEqPlayback();
                } else if (eqHasOutput) {
                  startEqPlayback(getCurrentTime());
                }
              } else {
                stopEqPlayback();
              }
              playPause();
            }}
            disabled={!isReady || !!error}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="5" y="4" width="5" height="16" rx="1.5" />
                <rect x="14" y="4" width="5" height="16" rx="1.5" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <polygon points="7,5 19,12 7,19" />
              </svg>
            )}
          </button>
          <div className="advanced-editor__wave">
            <div className="waveform waveform--large" ref={waveformRef} />
          </div>
        </section>

        <section className="advanced-editor__grid">
          <div className="editor-card">
            <h3>Pitch and tempo</h3>
            <p className="file-meta">
              Independent pitch/tempo controls and formant-safe options.
            </p>
            <div className="editor-card__sliders">
              <label>
                Pitch
                <input
                  type="range"
                  min="-12"
                  max="12"
                  step="1"
                  value={pitchSemitones}
                  onChange={(event) => setPitchSemitones(Number(event.target.value))}
                />
                <span className="control-value">
                  {pitchSemitones > 0 ? '+' : ''}{pitchSemitones} st
                </span>
              </label>
              <label>
                Speed
                <input
                  type="range"
                  min="60"
                  max="160"
                  step="1"
                  value={speedPercent}
                  onChange={(event) => setSpeedPercent(Number(event.target.value))}
                />
                <span className="control-value">{speedPercent}%</span>
              </label>
            </div>
          </div>
          <div className="editor-card">
            <h3>Equalizer</h3>
            <p className="file-meta">
              Realtime 3-band EQ with mute and solo controls.
            </p>
            <div className="editor-card__controls editor-card__controls--center">
              <button
                className={`eq-toggle ${eqEnabled ? 'eq-toggle--on' : ''}`}
                type="button"
                onClick={() => setEqEnabled((current) => !current)}
                disabled={!isEqReady}
                aria-pressed={eqEnabled}
              >
                <span className="eq-toggle__label">EQ</span>
                <span className="eq-toggle__track">
                  <span className="eq-toggle__thumb" />
                </span>
              </button>
            </div>
            {isEqReady ? (
              <div className="band-list">
                {bands.map((band) => (
                  <div className="band-item" key={band.id}>
                    <span>{band.name}</span>
                    <div className="band-actions">
                      <button
                        type="button"
                        className={`chip ${band.muted ? 'chip-active' : ''}`}
                        onClick={() => toggleBandMute(band.id)}
                        disabled={!eqEnabled}
                      >
                        {band.muted ? 'Muted' : 'Mute'}
                      </button>
                      <button
                        type="button"
                        className={`chip ${band.solo ? 'chip-active' : ''}`}
                        onClick={() => toggleBandSolo(band.id)}
                        disabled={!eqEnabled}
                      >
                        {band.solo ? 'Soloed' : 'Solo'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="status">Preparing EQ...</p>
            )}
            {eqEnabled && !eqHasOutput ? (
              <p className="status">All bands muted.</p>
            ) : null}
          </div>
          <div className="editor-card">
            <h3>Crop and slice</h3>
            <p className="file-meta">
              Define regions, trim edges, and export slices.
            </p>
            <div className="editor-card__sliders">
              <label>
                Crop start
                <input
                  type="range"
                  min="0"
                  max={duration || 0}
                  step="0.01"
                  value={cropStart}
                  onChange={(event) =>
                    handleCropStart(Number(event.target.value))
                  }
                  disabled={!isReady}
                />
                <span className="control-value">{formatTime(cropStart)}</span>
              </label>
              <label>
                Crop end
                <input
                  type="range"
                  min="0"
                  max={duration || 0}
                  step="0.01"
                  value={cropEnd}
                  onChange={(event) => handleCropEnd(Number(event.target.value))}
                  disabled={!isReady}
                />
                <span className="control-value">{formatTime(cropEnd)}</span>
              </label>
            </div>
            <div className="editor-card__controls">
              <button
                className="ghost icon-button"
                type="button"
                onClick={setStartToPlayhead}
                disabled={!isReady}
                aria-label="Set start to playhead"
                title="Set start to playhead"
              >
                <IconSetStart />
              </button>
              <button
                className="ghost icon-button"
                type="button"
                onClick={setEndToPlayhead}
                disabled={!isReady}
                aria-label="Set end to playhead"
                title="Set end to playhead"
              >
                <IconSetEnd />
              </button>
            </div>
            <div className="editor-card__controls">
              <button
                className="ghost icon-button"
                type="button"
                onClick={() => playSegmentWithEq(cropStart, cropEnd)}
                disabled={!isReady}
                aria-label="Play crop"
                title="Play crop"
              >
                <IconPlay />
              </button>
              <button
                className="ghost icon-button"
                type="button"
                onClick={addSlice}
                disabled={!isReady}
                aria-label="Add slice"
                title="Add slice"
              >
                <IconAdd />
              </button>
            </div>
            {slices.length ? (
              <div className="slice-list">
                {slices.map((slice) => (
                  <div className="slice-item" key={slice.id}>
                    <div>
                      <strong>{slice.label}</strong>
                      <p className="file-meta">
                        {formatTime(slice.start)} - {formatTime(slice.end)}
                      </p>
                    </div>
                    <div className="slice-actions">
                      <button
                        className="chip icon-button"
                        type="button"
                        onClick={() =>
                          playSegmentWithEq(slice.start, slice.end)
                        }
                        aria-label={`Play ${slice.label}`}
                        title={`Play ${slice.label}`}
                      >
                        <IconPlay />
                      </button>
                      <button
                        className="chip icon-button"
                        type="button"
                        onClick={() => downloadSliceMp3(slice)}
                        aria-label={`Download ${slice.label} as MP3`}
                        title={`Download ${slice.label} as MP3`}
                      >
                        <IconDownload />
                      </button>
                      <button
                        className="chip icon-button"
                        type="button"
                        onClick={() => removeSlice(slice.id)}
                        aria-label={`Remove ${slice.label}`}
                        title={`Remove ${slice.label}`}
                      >
                        <IconRemove />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="status">No slices yet.</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdvancedAudioEditor;
